import _ from 'lodash'
import JSONWrapper from './json-wrapper'
import courseSchema from '../../../public/json/course.schema.json'
import typeSchema from '../../../public/json/types.json'
import { ValidationError, ErrorSeverity } from './error'

const defaultStage = (name) => ({
  name,
  rolesOnPanel: [],
  steps: [],
  marks: {
    5: 1,
    4: 3,
    3: 5
  }
})

/* eslint-disable no-param-reassign */
function arrayMove(arr, oldIndex, newIndex) {
  while (oldIndex < 0) {
    oldIndex += arr.length
  }
  while (newIndex < 0) {
    newIndex += arr.length
  }
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1
    // eslint-disable-next-line no-plusplus
    while (k--) {
      arr.push(undefined)
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0])
  return arr
}

/**
 * Поиск дубликатов в массиве
 *
 * @param {Array} arr
 * @returns {Array}
 */
const findDuplicates = (arr) => {
  const sortedArr = arr.slice().sort()
  const results = []
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < sortedArr.length - 1; i++) {
    if (sortedArr[i + 1] === sortedArr[i]) {
      results.push(sortedArr[i])
    }
  }
  return results
}

/**
 * Класс курса
 *
 * @extends {JSONWrapper}
 */
class Course extends JSONWrapper {
  constructor(data) {
    super(data)
    this.setSchemas(courseSchema, [typeSchema])
    this.codeAccessor = (o) => o.code
  }

  afterObjectSet() {
    this.importCode((o) => o.code)
  }

  /**
   * Удалить этап
   *
   * @param {number} index Индекс этапа
   */
  deleteStage(index) {
    this.stages.splice(index, 1)
  }

  /**
   * Проверка корректности roles
   *
   * @param {Array.<string>?} roles
   * @returns {Array.<ValidationError>}
   */
  validateRoles(roles) {
    roles = roles || this.roles
    const errors = findDuplicates(roles).map(
      (role) => new ValidationError(
        `Название роли повторяется: ${role}`,
        null,
        `/roles/${roles.indexOf(role)}`,
      ),
    )
    roles.forEach((role, index) => {
      if (role.length === 0) {
        errors.push(
          new ValidationError(
            'Пустое название роли',
            ValidationError.WARN,
            `/roles/${index}`,
          ),
        )
      }
    })
    return errors
  }

  /**
   * Проверка корректности /stages/-/rolesOnPanel
   *
   * @param {Array} rolesOnPanel
   * @param {Array.<number>} panelIds Правильные id пультов
   * @param {Array.<string>} availableRoles Правильные роли
   * @param {string} stagePath jsonPath до этапа
   * @returns {Array.<ValidationError>}
   */
  static validateStageRolesOnPanel(
    rolesOnPanel,
    panelIds,
    availableRoles,
    stagePath,
  ) {
    const usedPanels = new Set()
    const usedRoles = new Set()
    const panels = new Set(panelIds || [])
    const roles = new Set(availableRoles)
    const errors = []
    const stageId = stagePath.split('/')[2]
    rolesOnPanel.forEach(({ role, panel }, index) => {
      const path = `${stagePath}/rolesOnPanel/${index}`
      if (usedPanels.has(panel)) {
        errors.push(
          new ValidationError(
            `Пульт {id: ${panel}} уже назначен на роль в этапе ${stageId}`,
            'warn',
            path,
          ),
        )
      }
      if (panels.size > 0 && !panels.has(panel)) {
        errors.push(
          new ValidationError(`Пульт {id: ${panel}} не существует`, null, path),
        )
      }
      if (!roles.has(role)) {
        errors.push(
          new ValidationError(`Роли "${role}" не существует`, null, path),
        )
      }
      usedPanels.add(panel)
      usedRoles.add(role)
    })
    new Set([...roles].filter((role) => !usedRoles.has(role))).forEach(
      (unusedRole) => {
        errors.push(
          new ValidationError(
            `Роль "${unusedRole}" не использована в этапе ${stageId}`,
            ErrorSeverity.INFO,
            stagePath,
          ),
        )
      },
    )
    new Set([...panels].filter((panel) => !usedPanels.has(panel))).forEach(
      (unusedPanel) => {
        errors.push(
          new ValidationError(
            `Пульт {id: ${unusedPanel}} не использован в этапе ${stageId}`,
            ErrorSeverity.INFO,
            stagePath,
          ),
        )
      },
    )
    return errors
  }

  /**
   * Валидация rolesOnPanel для всех этапов
   *
   * @param {Object} object
   * @param {Array.<number>?} panelIds Правильные id пультов
   * @returns {Array.<ValidationError>}
   */
  validateRolesOnPanel(object, panelIds) {
    const errors = []
    object = object || this.object
    object.stages.forEach((stage, index) => {
      errors.push(
        ...Course.validateStageRolesOnPanel(
          stage.rolesOnPanel,
          panelIds,
          object.roles,
          `/stages/${index}`,
        ),
      )
    })
    return errors
  }

  /**
   * Валидация всех шагов курса
   *
   * @param {Object} object
   * @param {Object} params Параметры валидации
   * @param {Array.<Panel>} params.panel связанные пульты
   * (можно получить из ${@link ElementsCollection.itemsById})
   * @returns {Array.<ValidationError>}
   */
  validateStages(object, params) {
    object = object || this.object
    const errors = []
    const usedStageNames = new Set()
    object.stages.forEach((stage, stageIndex) => {
      const stagePath = `/stages/${stageIndex}`
      if (usedStageNames.has(stage.name)) {
        errors.push(
          new ValidationError(
            `Этап с именем "${stage.name}" уже существует`,
            null,
            `${stagePath}/name`,
          ),
        )
      } else {
        usedStageNames.add(stage.name)
      }
      if (stage.stageName) {
        const linkedStage = object.stages.find(
          (s) => s.name === stage.stageName,
        )
        if (!linkedStage) {
          errors.push(
            new ValidationError(
              `Не найден этап с именем "${stage.stageName}"`,
              null,
              `${stagePath}/stageName`,
            ),
          )
        } else {
          const linkedSteps = new Set(linkedStage.steps.map((step) => step.id))
          if (!linkedSteps.has(stage.startStep)) {
            errors.push(
              new ValidationError(
                `Не найден шаг ${stage.startStep} в этапе ${linkedStage.name}`,
                null,
                `${stagePath}/startStep`,
              ),
            )
          }
          if (stage.endStep !== undefined && !linkedSteps.has(stage.endStep)) {
            errors.push(
              new ValidationError(
                `Не найден шаг ${stage.endStep} в этапе ${linkedStage.name}`,
                null,
                `${stagePath}/startStep`,
              ),
            )
          }
        }
      }
      errors.push(...this.validateSteps(stage, object, stagePath, params))
    })
    return errors
  }

  /**
   * Валидация шагов одного этапа
   *
   * @param {Object} stage Элемент Course.stages
   * @param {Object} object Объект курса. Если нет, берется из объекта класса
   * @param {string} stagePath Путь до этапа
   * @param {Object} params Параметры валидации
   * @param {Object.<number, Panel>} params.panelsById Связанные пульты
   * @param {Object.<number, Object>} globalItems Элементы коллекции
   * (можно получить из ${@link ElementsCollection.itemsById})
   * @param {Object} params.module модуль для проверки функций. Если нет, берется из объекта класса
   * @returns {Array.<ValidationError>}
   */
  validateSteps(stage, object, stagePath, params) {
    object = object || this.object
    const errors = []
    const usedStepIds = new Set()
    const userStepNumbers = new Set()
    const availableRoles = new Set(object.roles)
    stage.steps.forEach((step, stepIndex) => {
      const path = `${stagePath}/steps/${stepIndex}`
      if (usedStepIds.has(step.id)) {
        errors.push(
          new ValidationError(
            `Шаг {id: ${step.id}} уже существует`,
            null,
            `${path}/id`,
          ),
        )
      } else {
        usedStepIds.add(step.id)
      }
      if (userStepNumbers.has(step.number)) {
        errors.push(
          new ValidationError(
            `Шаг {number: ${step.number}} уже существует`,
            null,
            `${path}/id`,
          ),
        )
      } else {
        userStepNumbers.add(step.number)
      }
      if (!availableRoles.has(step.role)) {
        errors.push(
          new ValidationError(
            `Роли "${step.role}" не существует`,
            null,
            `${path}/role`,
          ),
        )
      }
      errors.push(...this.validateAction(step, path, params))
    })
    return errors
  }

  /**
   * Проверка одного действия
   *
   * @param {Object} step Проверяемый шаг
   * @param {string} path JSONPointer до шага
   * @param {Object} params Параметры валидации
   * @param {Object.<number, Panel>} params.panelsById Связанные пульты
   * @param {Object.<number, Object>} globalItems Элементы коллекции
   * (можно получить из ${@link ElementsCollection.itemsById})
   * @param {Object} params.module модуль для проверки функций. Если нет, берется из объекта класса
   * @returns {Array.<ValidationError>}
   */
  validateAction(step, path, params) {
    const errors = []
    if (!this.moduleHasExport(step.action.function, params.module)) {
      errors.push(
        new ValidationError(
          `Функция "${step.action.function}" не найдена в коде`,
          null,
          `${path}/action/function`,
        ),
      )
    }
    const actionPanel = params.panelsById[step.action.panelId]
    if (!actionPanel) {
      errors.push(
        new ValidationError(
          `Пульта {id: ${step.action.panelId}} не существует`,
          null,
          `${path}/action/panelId`,
        ),
      )
      return errors
    }
    const panelItem = actionPanel.itemsById[step.action.itemId]
    if (!panelItem) {
      errors.push(
        new ValidationError(
          `Элемента {id: ${step.action.itemId}} не существует на пульте {id: ${
            step.action.panelId
          }}`,
          null,
          `${path}/action/itemId`,
        ),
      )
      return errors
    }
    const actionItem = params.globalItems[panelItem.itemId]
    if (!actionItem) {
      errors.push(
        new ValidationError(
          `Элемент {id: ${
            panelItem.itemId
          }} не существует в коллекции (ссылка из элемента {id: ${
            panelItem.id
          }} пульта {id: ${actionPanel.id}})`,
          null,
          `${path}/action/itemId`,
        ),
      )
    } else if (!actionItem.activeZones) {
      errors.push(
        new ValidationError(
          `Элемент в коллекции {id: ${
            actionItem.id
          }} не интерактивен (нет атрибута activeZones)`,
          null,
          `${path}/action`,
        ),
      )
    } else {
      const activeZoneIds = new Set(
        actionItem.activeZones.map((activeZone) => activeZone.id),
      )
      if (!activeZoneIds.has(step.action.activeZoneId)) {
        errors.push(
          new ValidationError(
            `У элемента в коллекции {id: ${
              actionItem.id
            }} не существует активной зоны {id: ${step.action.activeZoneId}}`,
            null,
            `${path}/action/activeZoneId`,
          ),
        )
      }
    }
    return errors
  }

  validateNewResources(object, params) {
    object = object || this.object
    const errors = []
    params.resources.resources.forEach((resource) => {
      if (resource.new) {
        errors.push(
          new ValidationError(
            `Ресурс {id: ${
              resource.id
            }} недавно добавлен. Его id изменится после отправки на сервер. Нажмите "Сохранить"`,
            'warn',
            '/',
          ),
        )
      }
    })
    return errors
  }

  validateName(object) {
    object = object || this.object
    const errors = []
    if (_.isNil(object.name) || object.name.length === 0) {
      errors.push(new ValidationError('Имя не может быть пустым', 'warn', '/name'))
    }
    return errors
  }

  /**
   * Валидация объекта курса
   *
   * @param {Object} object Если нет, то берется из объекта класса
   * @param {Object} params Параметры валидации
   * @param {Array.<number>} params.panelIds id доступных пультов
   * @param {Array.<Panel>} params.panel связанные пульты
   * (можно получить из {@link ElementsCollection.itemsById})
   * @returns {Array.<ValidationError>}
   */
  validateObject(object, params) {
    object = object || this.object
    params = params || {}
    const schemaErrors = super.validateObject(object, params)
    if (schemaErrors.length > 0) {
      return schemaErrors
    }
    return [
      ...this.validateName(object),
      ...this.validateNewResources(object, params),
      ...this.validateRoles(object.roles),
      ...this.validateRolesOnPanel(object, params.panelIds),
      ...this.validateStages(object, params)
    ]
  }

  /**
   * Добавить этап
   *
   * @param {string} name Имя этапа
   */
  addStage(name) {
    this.stages.push({
      ...defaultStage(name)
    })
  }

  /**
   * Добавить нештатную ситуацию
   *
   * @param {string} name Имя этапа
   * @param {string} stageName имя этапа начала
   * @param {number} startStep Шаг выбранного этапа
   * @param {(number|null)} endStep Шаг этапа окончания
   */
  addUnexpectedStage(name, stageName, startStep, endStep) {
    const newStage = {
      ...defaultStage(name),
      stageName,
      startStep
    }
    if (endStep !== undefined && endStep !== null) {
      newStage.endStep = endStep
    }
    this.stages.push(newStage)
  }

  /**
   * Передвинуть этап
   *
   * @param {number} index старый индекс
   * @param {number} newIndex Новый индекс
   */
  moveStage(index, newIndex) {
    arrayMove(this.stages, index, newIndex)
  }

  // eslint-disable-next-line class-methods-use-this
  getPanelsForStage(stage) {
    return new Set(stage.rolesOnPanel.map((roleOnPanel) => roleOnPanel.panel))
  }
}

export default Course
